import { useState, useEffect, useCallback } from 'react';

const cache = {
  profile: { data: null, fetchedAt: 0 },
};

let inFlightProfile = null;

// TTL: 1 minute (profile changes less frequently)
const PROFILE_TTL_MS = 60000;

// Listeners: any mounted hook instance registers a forceUpdate here
const listeners = new Set();
function notifyListeners() {
  listeners.forEach((fn) => fn());
}

const SIDEBAR_PROFILE_CACHE_KEY = 'influencer_profile_cache';
const SIDEBAR_PROFILE_CACHE_TIMESTAMP_KEY = 'influencer_profile_cache_timestamp';

function syncSidebarLocalStorageCache(data) {
  if (!data) return;
  try {
    localStorage.setItem(SIDEBAR_PROFILE_CACHE_KEY, JSON.stringify({
      fullName: data.full_name || '',
      profileImage: data.profileImage || '',
      profilePictureUrl: data.profile_picture_url || '',
    }));
    localStorage.setItem(SIDEBAR_PROFILE_CACHE_TIMESTAMP_KEY, Date.now().toString());
  } catch {
    // Ignore storage quota / private-mode failures
  }
}

/** Shared profile fetch with module TTL + single-flight (used by shell callers). */
export async function fetchInfluencerProfile(force = false) {
  const now = Date.now();
  if (!force && cache.profile.fetchedAt && now - cache.profile.fetchedAt < PROFILE_TTL_MS) {
    return cache.profile.data;
  }

  // Dedupe: if a request is already in-flight, wait for it
  if (inFlightProfile) {
    return inFlightProfile;
  }

  // Assign in-flight immediately so parallel shell callers cannot start a second request.
  inFlightProfile = (async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Missing authentication token');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/influencer/profile`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        cache.profile = { data, fetchedAt: Date.now() };
        syncSidebarLocalStorageCache(data);
        return data;
      }
      throw new Error('Failed to fetch profile');
    } finally {
      inFlightProfile = null;
    }
  })();

  return inFlightProfile;
}

/**
 * Shared hook for influencer profile data.
 * Uses module-level cache so data is shared across all mounted components.
 *
 * @returns {{ profile: Object|null, loading: boolean, error: string|null, refresh: Function, lastUpdated: number }}
 */
/** Forces a profile refetch and updates every mounted hook instance. */
export async function refreshInfluencerProfileCache() {
  const data = await fetchInfluencerProfile(true);
  notifyListeners();
  return data;
}

/** Resets module cache (for tests and rare logout flows). */
export function resetInfluencerProfileCache() {
  cache.profile = { data: null, fetchedAt: 0 };
  inFlightProfile = null;
}

export default function useInfluencerProfile() {
  const [profile, setProfile] = useState(cache.profile.data);
  const [loading, setLoading] = useState(!cache.profile.fetchedAt);
  const [error, setError] = useState(null);

  // Sync local state from module cache
  const syncFromCache = useCallback(() => {
    setProfile(cache.profile.data);
  }, []);

  // Register this instance as a listener
  useEffect(() => {
    listeners.add(syncFromCache);
    return () => {
      listeners.delete(syncFromCache);
    };
  }, [syncFromCache]);

  // Refresh function callable by consumers
  const refresh = useCallback(async (force = false) => {
    try {
      setError(null);
      const data = await fetchInfluencerProfile(force);
      setProfile(data);
      notifyListeners();
    } catch (err) {
      setError(err.message || 'Failed to load profile.');
    }
  }, []);

  // Initial fetch on mount
  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      if (cache.profile.fetchedAt) {
        setProfile(cache.profile.data);
        setLoading(false);

        // Refresh in background if stale
        const now = Date.now();
        if (now - cache.profile.fetchedAt >= PROFILE_TTL_MS) {
          try {
            await refresh(false);
          } catch {
            // Ignore background refresh errors
          }
        }
        return;
      }

      setLoading(true);
      try {
        const data = await fetchInfluencerProfile(false);
        if (!cancelled) {
          setProfile(data);
          notifyListeners();
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load profile.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    profile,
    loading,
    error,
    refresh,
    lastUpdated: cache.profile.fetchedAt,
  };
}
